import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';
import { TextKey, DataTestId } from '@mews-commander/enums';
import { LocalizedText } from '@mews/framework';
import { Reservation, ReservationTimeState } from '@mews-commander/data-access';
import { Loader } from '@mews-commander/ui';
import { Button, Container, getSpacingToken, getToken, Stack } from '@optimus-web/core';
import { ApplicationState, CommanderThunkDispatch } from '@mews-commander/types';
import { getCurrentEnterpriseReservation } from '@mews-commander/legacy-entities/src/selectors/reservations';
import { showReservationDetail } from '../../../routes';
import { ReservationTab } from '../../../../types/reservationTab';
import ReservationInfo from './ReservationInfo';
import { ReservationPrimaryAction } from './ReservationPrimaryAction';

const SmartDetailPositioner = styled(Container)`
    border-top: solid 1px ${getToken('borderStaticSecondary')};
    background: ${getToken('paletteBasicsWhite')};
    padding: ${getSpacingToken('200')}px;
`;

interface StateProps {
    reservation: Reservation | undefined;
}

interface DispatchProps {
    openReservationDetail: () => void;
}

interface OwnProps {
    reservationId: string;
    onUpdate: () => void;
}

const SmartDetail = ({
    reservationId,
    reservation,
    openReservationDetail,
    onUpdate,
}: StateProps & DispatchProps & OwnProps) => {
    if (!reservation) {
        return (
            <Container padding="200">
                <Loader />
            </Container>
        );
    }

    const { reservationTimeState, assignedResourceId } = reservation;
    const manageButtonVariant =
        reservationTimeState === ReservationTimeState.Past ||
        (reservationTimeState === ReservationTimeState.ToBeCheckedIn && assignedResourceId === null) ||
        reservationTimeState === ReservationTimeState.CheckedIn ||
        reservationTimeState === ReservationTimeState.Future
            ? 'primary'
            : 'tertiary';

    return (
        <>
            <Container padding="200">
                <ReservationInfo reservation={reservation} />
            </Container>
            <SmartDetailPositioner position="sticky" bottom="0" data-test-smart-detail-footer>
                <Stack distribution="space-between">
                    <Button
                        variant={manageButtonVariant}
                        onClick={openReservationDetail}
                        data-test-id={DataTestId.SmartDetailManageReservationButton}
                        type="submit"
                    >
                        <LocalizedText textKey={TextKey.Manage} />
                    </Button>
                    <ReservationPrimaryAction
                        reservationId={reservationId}
                        timeState={reservationTimeState}
                        onUpdate={onUpdate}
                    />
                </Stack>
            </SmartDetailPositioner>
        </>
    );
};

export default connect<StateProps, DispatchProps, OwnProps, ApplicationState>(
    createStructuredSelector({
        reservation: (state: ApplicationState, { reservationId }: OwnProps) =>
            getCurrentEnterpriseReservation(state, { id: reservationId }),
    }),
    (dispatch: CommanderThunkDispatch, { reservationId }: OwnProps) => ({
        openReservationDetail: () =>
            dispatch(showReservationDetail({ reservationId, reservationTab: ReservationTab.Status })),
    })
)(SmartDetail);
